/**
 * QdrantLiveSearch Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Advanced vector-based search interface for the Irish Funds Member Portal with comprehensive features:
 * - Real-time Qdrant vector database search with semantic similarity
 * - Dual view modes: Card view for detailed browsing, Table view for quick scanning
 * - Smart content type filtering (News, Repository, Working Groups, Events)
 * - Enhanced relevance scoring with IFIA brand color-coded indicators
 * - Submitted article detection and purple badge styling
 * - Sortable table columns (Title, Type, Relevance, Date, Summary)
 * - Intelligent modal routing based on content type
 * - URL-based state management with search parameter persistence
 * - Responsive pagination with dynamic items per page
 * - Rich tooltips with extended content previews
 * - Professional error handling and loading states
 * - Accessibility features with keyboard navigation
 * - Production-ready with comprehensive null safety checks
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import apiHelper from '../../utils/apiHelper';
import { processText } from '../../utils/textUtils';
import './QdrantLiveSearch.css';
import PDFModal from '../../modals/PDFModal';
import NewsModal from '../../modals/NewsModal';
import EventsModal from '../../modals/EventsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faFileAlt, faHistory, faCalendar, faCheckCircle, faExclamationCircle, faTimesCircle, faUsers, faNewspaper, faGlobe, faMousePointer, faThLarge, faList, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';


/**
 * Formats working group overview text for display
 * 
 * Processes raw working group text by removing metadata prefixes,
 * adding proper formatting with bold headers and line breaks
 * @param {string} text - Raw working group text content
 * @returns {string} Formatted HTML string ready for dangerouslySetInnerHTML
 */
function formatWorkingGroupOverview(text) {
  if (!text) return '';
  // Remove everything before [Summary]
  const summaryIndex = text.indexOf('[Summary]');
  let main = summaryIndex !== -1 ? text.slice(summaryIndex) : text;

  // Bold Summary: and add a break after the colon
  main = main.replace(/\[Summary\]/g, '<strong>Summary:</strong><br>');
  // Bold Terms of Reference: and add a break after the colon
  main = main.replace(/\[Terms of Reference\]/g, '<strong>Terms of Reference:</strong><br>');
  // Bold Objectives: and add a break after
  main = main.replace(/(Objectives:)/g, '<strong>$1</strong><br>');
  // Bold Updates: and add a break after
  main = main.replace(/(Updates:)/g, '<strong>$1</strong><br>');

  // Convert newlines to <br>
  main = main.replace(/\n/g, '<br>');

  return main;
}

/**
 * Enhanced source type mapping with IFIA brand colors
 * 
 * Maps document types to visual styling information including icons,
 * colors, and labels following IFIA brand guidelines
 * @param {Object} doc - Document metadata object
 * @returns {Object} Style configuration with type, icon, colors, and label
 */
const getSourceTypeInfo = (doc) => {
  if (doc.type === 'Event' || doc.eventDate || doc.EventDate) {
    return { 
      type: 'event', 
      icon: faCalendar, 
      color: '#bd2130', // IFIA Red
      bgColor: '#fdf2f2',
      label: 'Event',
      textColor: '#bd2130'
    };
  }
  if (doc.type === 'Working Group') {
    return { 
      type: 'working-group', 
      icon: faUsers, 
      color: '#006d92', // IFIA Blue
      bgColor: '#f0f8fb',
      label: 'Working Group',
      textColor: '#006d92'
    };
  }
  
  if (doc.type === 'Working Group Update') {
    return { 
      type: 'working-group-update', 
      icon: faUsers, 
      color: '#006d92', // IFIA Blue (same as Working Group)
      bgColor: '#f0f8fb',
      label: 'WG Update',
      textColor: '#006d92'
    };
  }
  
  // Handle submitted articles first (most specific check)
  if (doc.type === 'Submitted Article') {
    return { 
      type: 'submitted-article', // Unique type for CSS styling
      icon: faNewspaper, 
      color: '#8b5cf6', // Purple for submitted articles
      bgColor: '#f3f4f6',
      label: 'Submitted Article',
      textColor: '#8b5cf6'
    };
  }
  
  // Handle regular news articles
  if (doc.type === 'News') {
    return { 
      type: 'news', 
      icon: faNewspaper, 
      color: '#eb7b15', // IFIA Orange
      bgColor: '#fef7f0',
      label: 'News',
      textColor: '#eb7b15'
    };
  }
  
  return { 
    type: 'repository', 
    icon: faFileAlt, 
    color: '#56b32f', // IFIA Green
    bgColor: '#f3f8f3',
    label: 'Repository',
    textColor: '#385724' // IFIA Dark Green
  };
};

/**
 * Enhanced relevance score indicator with IFIA brand colors
 * 
 * Maps relevance scores to visual indicators with color-coded thresholds
 * @param {number} score - Relevance score (0-100)
 * @returns {Object} Style configuration with CSS class, icon, and color
 */
const getScoreIndicator = (score) => {
  if (score >= 80) return { class: 'score-excellent', icon: faCheckCircle, color: '#56b32f' }; // IFIA Green
  if (score >= 60) return { class: 'score-good', icon: faCheckCircle, color: '#eb7b15' }; // IFIA Orange
  if (score >= 40) return { class: 'score-fair', icon: faExclamationCircle, color: '#006d92' }; // IFIA Blue
  return { class: 'score-low', icon: faTimesCircle, color: '#bd2130' }; // IFIA Red
};

/**
 * QdrantLiveSearch Component
 * 
 * Main search interface providing comprehensive vector-based search functionality
 * with advanced filtering, sorting, and display options. Features include:
 * 
 * - Real-time semantic search using Qdrant vector database
 * - Dual view modes: Card view (detailed) and Table view (compact)
 * - Content type filtering: News, Repository, Working Groups, Events
 * - URL state management with search parameter persistence
 * - Dynamic pagination with view-specific item counts
 * - Sortable table columns with visual sort indicators
 * - Enhanced relevance scoring with IFIA brand color coding
 * - Modal integrations for different content types
 * - Responsive design with accessibility features
 * - Professional error handling and loading states
 * 
 * @returns {JSX.Element} Complete search interface component
 */
function QdrantLiveSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedType, setSelectedType] = useState('all');
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchedQuery, setLastSearchedQuery] = useState('');
  const getItemsPerPage = () => activeTabIndex === 0 ? 10 : 20; // Dynamic pagination: 10 for cards, 20 for table
  const [activeNewsId, setActiveNewsId] = useState(null);
  const [activeNewsType, setActiveNewsType] = useState(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [modals, setModals] = useState({});
  const [sortBy, setSortBy] = useState('date');
  const [activeWorkingGroup, setActiveWorkingGroup] = useState(null);
  const [showWorkingGroupModal, setShowWorkingGroupModal] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [typeTotals, setTypeTotals] = useState({});
  const [allResults, setAllResults] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0); // View mode: 0 = Card View, 1 = Table View
  const [tableSortBy, setTableSortBy] = useState('score'); // Current table sort column
  const [tableSortOrder, setTableSortOrder] = useState('desc'); // Sort direction: 'asc' or 'desc'

  const location = useLocation();
  const navigate = useNavigate();

  // Helper function to filter out documents marked as doNotShowInSources
  const filterOutHiddenDocuments = (results) => {
    return results.filter(r => {
      // Hide documents marked as doNotShowInSources
      if (r.payload.doNotShowInSources === true || r.payload.doNotShowInSources === 'true') {
        return false;
      }
      return true;
    });
  };

  // Read query parameter on mount and when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get('query');
    const pageParam = searchParams.get('page');
    const typeParam = searchParams.get('type');

    if (queryParam) {
      setQuery(decodeURIComponent(queryParam));
      setCurrentPage(pageParam ? parseInt(pageParam) : 1);
      setSelectedType(typeParam || 'all');
      handleSearch(
        decodeURIComponent(queryParam),
        pageParam ? parseInt(pageParam) : 1,
        typeParam || 'all',
        false, // don't update URL, already in sync
        sortBy
      );
    }
  }, [location.search]);

  /**
   * Performs search using Qdrant vector database with comprehensive result processing
   * 
   * Handles search execution, URL state management, type filtering, and result pagination.
   * Maintains separate state for all results and filtered display results.
   * 
   * @param {string} searchQuery - Search query string
   * @param {number} page - Page number for pagination
   * @param {string} type - Content type filter ('all', 'News', 'Repository', etc.)
   * @param {boolean} updateUrl - Whether to update browser URL with search params
   * @param {string} sort - Sort order ('relevance', 'date', 'type')
   */
  const handleSearch = async (searchQuery = query, page = currentPage, type = 'all', updateUrl = true, sort = sortBy) => {
    setHasSearched(true);
    setLastSearchedQuery(searchQuery);
    setResults([]); // Clear results immediately when a new search starts
    try {
      setIsLoading(true);
      setError(null);

      // Update URL with search parameters if needed
      if (updateUrl) {
        const params = new URLSearchParams();
        if (searchQuery) params.set('query', searchQuery);
        if (page) params.set('page', page);
        // Always set type to 'all' in the URL for consistency
        params.set('type', 'all');
        if (sort) params.set('sortBy', sort);
        navigate({ search: params.toString() }, { replace: true });
      }

      // Always fetch all types from backend
      const searchResults = await apiHelper.fetchQdrantSearch(searchQuery, page, sort, 'desc', 'all');
      const allFetchedResults = searchResults.result || [];
      setAllResults(allFetchedResults);
      
      // Use typeTotals from backend (calculated from all valid results, not just limited results)
      // This ensures consistent counts regardless of sort order
      const backendTypeTotals = searchResults.typeTotals || {};
      
      // Ensure all type keys exist with defaults
      const totals = {
        News: backendTypeTotals['News'] || backendTypeTotals['Submitted Article'] || 0,
        Repository: backendTypeTotals['Repository'] || 0,
        'Working Group': backendTypeTotals['Working Group'] || 0,
        Event: backendTypeTotals['Event'] || 0
      };
      
      // If backend returned 'Submitted Article' separately, add it to News
      if (backendTypeTotals['Submitted Article']) {
        totals.News = (totals.News || 0) + (backendTypeTotals['Submitted Article'] || 0);
      }
      
      // If backend returned 'Working Group Update' separately, add it to Working Group
      if (backendTypeTotals['Working Group Update']) {
        totals['Working Group'] = (totals['Working Group'] || 0) + (backendTypeTotals['Working Group Update'] || 0);
      }
      
      setTypeTotals(totals);
      // Filter results for display, excluding documents marked as doNotShowInSources
      const filteredResults = filterOutHiddenDocuments(allFetchedResults);
      
      if (selectedType === 'all') {
        setResults(filteredResults);
      } else if (selectedType === 'Event') {
        setResults(filteredResults.filter(r => r.payload.type === 'Event' || r.payload.eventDate || r.payload.EventDate));
      } else if (selectedType === 'News') {
        setResults(filteredResults.filter(r => r.payload.type === 'News' || r.payload.type === 'Submitted Article'));
      } else if (selectedType === 'Working Group') {
        setResults(filteredResults.filter(r => r.payload.type === 'Working Group' || r.payload.type === 'Working Group Update'));
      } else {
        setResults(filteredResults.filter(r => r.payload.type === selectedType));
      }
      setTotalPages(Math.ceil((searchResults.total || 0) / getItemsPerPage()));
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
    
    // Filter out doNotShowInSources documents
    const filteredResults = filterOutHiddenDocuments(allResults);
    
    // Only filter on frontend, do not call backend
    if (newType === 'all') {
      setResults(filteredResults);
    } else if (newType === 'Event') {
      setResults(filteredResults.filter(r => r.payload.type === 'Event' || r.payload.eventDate || r.payload.EventDate));
    } else if (newType === 'News') {
      setResults(filteredResults.filter(r => r.payload.type === 'News' || r.payload.type === 'Submitted Article'));
    } else if (newType === 'Working Group') {
      setResults(filteredResults.filter(r => r.payload.type === 'Working Group' || r.payload.type === 'Working Group Update'));
    } else {
      setResults(filteredResults.filter(r => r.payload.type === newType));
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleSearch(query, newPage, selectedType, true, sortBy);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setCurrentPage(1);
    handleSearch(query, 1, selectedType, true, e.target.value);
  };

  // Handle tab change between Card and Table view
  const handleTabChange = (index) => {
    setActiveTabIndex(index);
    setCurrentPage(1); // Reset to first page when changing views
  };

  /**
   * Handles table column sorting with toggle functionality
   * 
   * Manages sort column and direction, toggling between ascending/descending
   * for the same column or setting new column with appropriate default order.
   * 
   * @param {string} column - Column name to sort by ('title', 'type', 'score', 'date')
   */
  const handleTableSort = (column) => {
    if (tableSortBy === column) {
      // Toggle sort order if same column
      setTableSortOrder(tableSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to desc for score, asc for others
      setTableSortBy(column);
      setTableSortOrder(column === 'score' ? 'desc' : 'asc');
    }
  };

  /**
   * Sorts search results for table view display
   * 
   * Applies sorting based on current table sort settings. Only affects
   * table view display - card view uses server-side sorting.
   * 
   * @param {Array} results - Array of search result objects
   * @returns {Array} Sorted array of results
   */
  const getSortedResults = (results) => {
    if (activeTabIndex !== 1) return results; // Only sort in table view
    
    return [...results].sort((a, b) => {
      let aVal, bVal;
      
      switch (tableSortBy) {
        case 'title':
          aVal = a.payload.title || '';
          bVal = b.payload.title || '';
          break;
        case 'type':
          aVal = a.payload.type || '';
          bVal = b.payload.type || '';
          break;
        case 'date':
          aVal = new Date(a.payload.publishDate || a.payload.eventDate || a.payload.EventDate || 0);
          bVal = new Date(b.payload.publishDate || b.payload.eventDate || b.payload.EventDate || 0);
          break;
        case 'score':
        default:
          aVal = a.score || 0;
          bVal = b.score || 0;
          break;
      }
      
      if (aVal < bVal) return tableSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return tableSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`qdrant-page-button ${currentPage === i ? 'qdrant-page-button-active' : ''}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="qdrant-pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="qdrant-page-button"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="qdrant-page-button"
        >
          Next
        </button>
      </div>
    );
  };

  /**
   * Renders content preview for search result cards
   * 
   * Processes and displays content based on document type with appropriate
   * formatting. Handles special cases for submitted articles, repository items,
   * and working groups with priority-based content selection.
   * 
   * @param {Object} metadata - Document metadata object
   * @returns {JSX.Element|null} Formatted content component or null if filtered
   */
  const renderContent = (metadata) => {
    // Only render if type matches selected type or if 'all' is selected
    const isEvent = metadata.type === 'Event' || metadata.eventDate || metadata.EventDate;
    const isNews = metadata.type === 'News' || metadata.type === 'Submitted Article';
    const isWorkingGroup = metadata.type === 'Working Group' || metadata.type === 'Working Group Update';
    
    if (selectedType !== 'all' && selectedType === 'Event' && !isEvent) {
      return null;
    }
    if (selectedType !== 'all' && selectedType === 'News' && !isNews) {
      return null;
    }
    if (selectedType !== 'all' && selectedType === 'Working Group' && !isWorkingGroup) {
      return null;
    }
    if (selectedType !== 'all' && selectedType !== 'Event' && selectedType !== 'News' && selectedType !== 'Working Group' && metadata.type !== selectedType) {
      return null;
    }

    if (metadata.type === 'News' || metadata.type === 'Submitted Article') {
      return (
        <div className="qdrant-content">
          <p className="qdrant-text">
            {processText(metadata.text)}
          </p>
          {metadata.type === 'Submitted Article' && (metadata.submittedAuthor || metadata.submittedCompany) && (
            <p className="qdrant-author">
              <strong>Author:</strong> {metadata.submittedAuthor && metadata.submittedCompany ? 
                `${metadata.submittedAuthor} (${metadata.submittedCompany})` :
                metadata.submittedAuthor || metadata.submittedCompany
              }
            </p>
          )}
        </div>
      );
    } else if (metadata.type === 'Repository') {
      const contentText = metadata.summary || metadata.description || metadata.content || metadata.abstractText || metadata.text;
      
      return (
        <div className="qdrant-content">
          {contentText ? (
            <p className="qdrant-text">
              {processText(contentText)}
            </p>
          ) : (
            <p className="qdrant-text">
              No content available
            </p>
          )}
        </div>
      );
    } else if (metadata.type === 'Working Group' || metadata.type === 'Working Group Update') {
      return (
        <div className="qdrant-content">
          <p className="qdrant-text">
            {processText(metadata.text || metadata.content)}
          </p>
        </div>
      );
    } else if (isEvent) {
      return (
        <div className="qdrant-content">
          <p className="qdrant-text">
            {processText(metadata.text || metadata.summary)}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderTooltip = (metadata) => {
    // Only render if type matches selected type or if 'all' is selected
    const isEvent = metadata.type === 'Event' || metadata.eventDate || metadata.EventDate;
    const isNews = metadata.type === 'News' || metadata.type === 'Submitted Article';
    const isWorkingGroup = metadata.type === 'Working Group' || metadata.type === 'Working Group Update';
    
    if (selectedType !== 'all' && selectedType === 'Event' && !isEvent) {
      return null;
    }
    if (selectedType !== 'all' && selectedType === 'News' && !isNews) {
      return null;
    }
    if (selectedType !== 'all' && selectedType === 'Working Group' && !isWorkingGroup) {
      return null;
    }
    if (selectedType !== 'all' && selectedType !== 'Event' && selectedType !== 'News' && selectedType !== 'Working Group' && metadata.type !== selectedType) {
      return null;
    }

    let content = '';
    if (isNews) {
      content = metadata.text;
      // Add author/company info for submitted articles
      if (metadata.type === 'Submitted Article' && (metadata.submittedAuthor || metadata.submittedCompany)) {
        content = `${content}\n\nAuthor: ${metadata.submittedAuthor || metadata.submittedCompany}`;
      }
    } else if (metadata.type === 'Repository') {
      content = metadata.summary || metadata.description || metadata.content || metadata.abstractText || metadata.text;
    } else if (metadata.type === 'Working Group' || metadata.type === 'Working Group Update') {
      content = metadata.text || metadata.content;
    } else if (isEvent) {
      content = metadata.text || metadata.summary;
    }

    // Process the content to remove HTML and meta tags, but show more content in tooltip
    return processText(content, 800); // Increased length for tooltip to show more content
  };

  /**
   * Opens NewsModal with intelligent article type detection
   * 
   * Handles both regular news articles and submitted articles with
   * smart ID extraction and type-aware modal configuration.
   * 
   * @param {Object} newsItem - News article metadata object
   */
  const handleNewsShow = (newsItem) => {
    const newsId = newsItem.documentId || newsItem.listItemId || newsItem.id;
    const articleType = newsItem.type === 'Submitted Article' ? 'submitted' : 'news';
    setActiveNewsId(newsId);
    setActiveNewsType(articleType);
    setShowNewsModal(true);
  };

  // Handler to open PDF modal
  const openPDFModal = (id, title, fileName) => {
    const maxZIndex = Math.max(...Object.values(modals).map(modal => modal.zIndex || 0), 0) + 1;
    setModals(prev => ({
      ...prev,
      [id]: {
        id,
        isOpen: true,
        title,
        pdfUrl: fileName,
        zIndex: maxZIndex
      }
    }));
  };

  // Handler to close PDF modal
  const closePDFModal = (id) => {
    setModals(prevModals => {
      const newModals = { ...prevModals };
      if (newModals[id]) {
        newModals[id].isOpen = false;
      }
      return newModals;
    });
  };

  // Handler to open PDF modal
  const handlePDFShow = (pdfItem) => {
    const pdfId = pdfItem.documentId || pdfItem.listItemId || pdfItem.id;
    const pdfTitle = pdfItem.title || 'Untitled Document';
    // Construct URL from fileName if available, otherwise use fileUrl or url
    let pdfFileName = null;
    if (pdfItem.fileName) {
      // If we have a fileName, construct a URL that the backend can parse
      pdfFileName = `https://example.com/documents/${encodeURIComponent(pdfItem.fileName)}`;
    } else {
      pdfFileName = pdfItem.fileUrl || pdfItem.url || null;
    }
    
    if (!pdfId) {
      console.error('Cannot open PDF modal: No valid ID found in pdfItem', pdfItem);
      return;
    }
    
    openPDFModal(pdfId, pdfTitle, pdfFileName);
  };

  // Handler to open Event modal
  const handleEventShow = (eventItem) => {
    const eventId = eventItem.documentId || eventItem.listItemId || eventItem.id;
    const formattedEvent = {
      ...eventItem,
      ID: eventId, // Capital ID - what EventsModal looks for first
      id: eventId,
      listItemId: eventId,
      Topics: Array.isArray(eventItem.topics) ? eventItem.topics : 
             (eventItem.topics ? [eventItem.topics] : 
             Array.isArray(eventItem.Topics) ? eventItem.Topics :
             (eventItem.Topics ? [eventItem.Topics] : []))
    };
    setActiveEvent(formattedEvent);
    setShowEventModal(true);
  };

  /**
   * Central click handler for search result cards and table rows
   * 
   * Routes user interactions to appropriate modal types based on content:
   * - Events: EventsModal with formatted event data
   * - News/Submitted Articles: NewsModal with type-aware routing
   * - Repository: PDFModal for document viewing
   * - Working Groups: WorkingGroupModal for preview
   * 
   * @param {Object} metadata - Document metadata object containing type and content info
   */
  const handleCardClick = (metadata) => {
    if (!metadata) {
      return;
    }

    const isEvent = metadata.type === 'Event' || metadata.eventDate || metadata.EventDate;
    const isNews = metadata.type === 'News' || metadata.type === 'Submitted Article';
    
    if (isEvent) {
      handleEventShow(metadata);
    } else if (isNews) {
      handleNewsShow(metadata);
    } else if (metadata.type === 'Repository') {
      handlePDFShow(metadata);
    } else if (metadata.type === 'Working Group') {
      setActiveWorkingGroup(metadata);
      setShowWorkingGroupModal(true);
    } else if (metadata.type === 'Working Group Update') {
      // Navigate to the working group page using workingGroupId
      if (metadata.workingGroupId) {
        navigate(`/WorkingGroups/WorkingGroup/${metadata.workingGroupId}`);
      } else {
        // Fallback: show in modal if no workingGroupId
        setActiveWorkingGroup(metadata);
        setShowWorkingGroupModal(true);
      }
    }
  };

  // Render table header with sortable columns
  const renderTableHeader = () => {
    const getSortIcon = (column) => {
      if (tableSortBy !== column) return faSort;
      return tableSortOrder === 'asc' ? faSortUp : faSortDown;
    };

    return (
      <thead>
        <tr>
          <th onClick={() => handleTableSort('title')} className="sortable-header">
            <span>Title</span>
            <FontAwesomeIcon icon={getSortIcon('title')} className="sort-icon" />
          </th>
          <th onClick={() => handleTableSort('type')} className="sortable-header">
            <span>Type</span>
            <FontAwesomeIcon icon={getSortIcon('type')} className="sort-icon" />
          </th>
          <th onClick={() => handleTableSort('score')} className="sortable-header">
            <span>Relevance</span>
            <FontAwesomeIcon icon={getSortIcon('score')} className="sort-icon" />
          </th>
          <th onClick={() => handleTableSort('date')} className="sortable-header">
            <span>Date</span>
            <FontAwesomeIcon icon={getSortIcon('date')} className="sort-icon" />
          </th>
          <th>Summary</th>
        </tr>
      </thead>
    );
  };

  // Render table view
  const renderTableView = () => {
    const sortedResults = getSortedResults(results);
    
    return (
      <div className="qdrant-table-container">
        <table className="qdrant-results-table">
          {renderTableHeader()}
          <tbody>
            {sortedResults
              .filter(result => {
                if (selectedType === 'all') return true;
                if (selectedType === 'Event') {
                  return result.payload.type === 'Event' || result.payload.eventDate || result.payload.EventDate;
                }
                if (selectedType === 'News') {
                  return result.payload.type === 'News' || result.payload.type === 'Submitted Article';
                }
                if (selectedType === 'Working Group') {
                  return result.payload.type === 'Working Group' || result.payload.type === 'Working Group Update';
                }
                return result.payload.type === selectedType;
              })
              .map((result, index) => {
                const metadata = result.payload;
                const typeInfo = getSourceTypeInfo(metadata);
                const scoreInfo = getScoreIndicator(result.score);
                
                return (
                  <tr 
                    key={index} 
                    className="qdrant-table-row"
                    onClick={() => handleCardClick(metadata)}
                  >
                    <td className="title-cell">
                      <div className="table-title">
                        {metadata.title}
                      </div>
                    </td>
                    <td className="type-cell">
                      <div 
                        className="table-type-badge" 
                        style={{ 
                          backgroundColor: typeInfo.bgColor, 
                          color: typeInfo.textColor 
                        }}
                      >
                        <FontAwesomeIcon icon={typeInfo.icon} />
                        <span>{typeInfo.label}</span>
                      </div>
                    </td>
                    <td className="score-cell">
                      <div className="table-score-badge" style={{ color: scoreInfo.color }}>
                        <FontAwesomeIcon icon={scoreInfo.icon} />
                        <span>{Math.round(result.score)}%</span>
                      </div>
                    </td>
                    <td className="date-cell">
                      {(metadata.publishDate || metadata.eventDate || metadata.EventDate) ? (
                        new Date(metadata.publishDate || metadata.eventDate || metadata.EventDate).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="summary-cell">
                      <div className="table-summary">
                        {processText(
                          // For repository items, prioritize clean summary over formatted text
                          metadata.type === 'Repository' ? 
                            (metadata.summary || metadata.description || metadata.content || metadata.abstractText || metadata.text) :
                            (metadata.text || metadata.summary || metadata.description || metadata.content || metadata.abstractText) || 
                          'No summary available', 
                          150
                        )}
                        {metadata.type === 'Submitted Article' && (metadata.submittedAuthor || metadata.submittedCompany) && (
                          <div className="table-author">
                            <strong>Author:</strong> {metadata.submittedAuthor || metadata.submittedCompany}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="qdrant-search-container">
      <div className="qdrant-search-input-container">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search..."
          className="qdrant-search-input"
        />
        <i className="fas fa-magnifying-glass qdrant-search-icon"></i>

        <button
          onClick={() => handleSearch()}
          className="qdrant-search-button"
          disabled={isLoading}
        >
          {isLoading ? "Searching..." : "Search"}
        </button>
      </div>

      {/* Always show filter controls, even if there are no results */}
      <div className="qdrant-filters-container">
        <div className="qdrant-type-filters">
          <div 
            className={`qdrant-type-filter ${selectedType === 'all' ? 'active' : ''}`}
            onClick={() => handleTypeChange('all')}
            tabIndex={0}
          >
            All Types
            <span className="qdrant-type-total">
              {Object.values(typeTotals).reduce((a, b) => a + b, 0)}
            </span>
          </div>
          <div 
            className={`qdrant-type-filter news ${selectedType === 'News' ? 'active' : ''} ${typeTotals['News'] === 0 ? 'disabled' : ''}`}
            {...(typeTotals['News'] > 0 ? { onClick: () => handleTypeChange('News'), tabIndex: 0 } : { tabIndex: -1 })}
          >
            News
            <span className="qdrant-type-total">({typeTotals['News'] || 0})</span>
          </div>
          <div 
            className={`qdrant-type-filter repository ${selectedType === 'Repository' ? 'active' : ''} ${typeTotals['Repository'] === 0 ? 'disabled' : ''}`}
            {...(typeTotals['Repository'] > 0 ? { onClick: () => handleTypeChange('Repository'), tabIndex: 0 } : { tabIndex: -1 })}
          >
            Repository
            <span className="qdrant-type-total">({typeTotals['Repository'] || 0})</span>
          </div>
          <div 
            className={`qdrant-type-filter working-group ${selectedType === 'Working Group' ? 'active' : ''} ${typeTotals['Working Group'] === 0 ? 'disabled' : ''}`}
            {...(typeTotals['Working Group'] > 0 ? { onClick: () => handleTypeChange('Working Group'), tabIndex: 0 } : { tabIndex: -1 })}
          >
            Working Groups
            <span className="qdrant-type-total">({typeTotals['Working Group'] || 0})</span>
          </div>
          <div 
            className={`qdrant-type-filter event ${selectedType === 'Event' ? 'active' : ''} ${typeTotals['Event'] === 0 ? 'disabled' : ''}`}
            {...(typeTotals['Event'] > 0 ? { onClick: () => handleTypeChange('Event'), tabIndex: 0 } : { tabIndex: -1 })}
          >
            Events
            <span className="qdrant-type-total">({typeTotals['Event'] || 0})</span>
          </div>
        </div>
        {/* Only show sort dropdown in Card view */}
        {activeTabIndex === 0 && (
          <div className="qdrant-sort-container">
            <label htmlFor="sort-by" className="qdrant-sort-label">Sort by:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={handleSortChange}
              className="qdrant-type-select"
            >
              <option value="relevance">Relevance</option>
              <option value="type">Type</option>
              <option value="date">Date</option>
            </select>
          </div>
        )}
        
        {/* View Controls */}
        {results.length > 0 && (
          <div className="qdrant-view-controls">
            <button 
              className={`view-btn ${activeTabIndex === 0 ? 'active' : ''}`}
              onClick={() => handleTabChange(0)}
              title="Card View"
            >
              <FontAwesomeIcon icon={faThLarge} />
            </button>
            <button 
              className={`view-btn ${activeTabIndex === 1 ? 'active' : ''}`}
              onClick={() => handleTabChange(1)}
              title="Table View"
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="qdrant-loading-spinner">
          <div className="qdrant-spinner-dual-ring"></div>
        </div>
      )}

      {error && (
        <div className="qdrant-error">
          {error}
        </div>
      )}

      {/* No results indicator - always show when there are no results, not loading, and no error */}
      {!isLoading && !error && hasSearched && results.length === 0 && (
        <div className="qdrant-no-results">
          <i className="fas fa-search qdrant-no-results-icon"></i>
          <p>No results found{lastSearchedQuery ? ` for "${lastSearchedQuery}"` : ''}</p>
          <p className="qdrant-no-results-suggestion">Try different keywords, filters, or check your spelling</p>
        </div>
      )}

      {/* Results and pagination - only show when there are results */}
      {results.length > 0 && (
        <div className="qdrant-results-container">
          {/* Card View */}
          {activeTabIndex === 0 && (
            <>
              <div className="qdrant-results">
                {results
                  .filter(result => {
                    if (selectedType === 'all') return true;
                    if (selectedType === 'Event') {
                      return result.payload.type === 'Event' || result.payload.eventDate || result.payload.EventDate;
                    }
                    if (selectedType === 'News') {
                      return result.payload.type === 'News' || result.payload.type === 'Submitted Article';
                    }
                    if (selectedType === 'Working Group') {
                      return result.payload.type === 'Working Group' || result.payload.type === 'Working Group Update';
                    }
                    return result.payload.type === selectedType;
                  })
                  .map((result, index) => {
                    const metadata = result.payload;
                    
                    return (
                      <div
                        key={index}
                        className={`qdrant-result-card ${getSourceTypeInfo(metadata).type}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCardClick(metadata);
                        }}
                        style={{
                          cursor: 'pointer',
                          position: 'relative'
                        }}
                      >
                        <div className="qdrant-result-header">
                          <div>
                            <h3 className="qdrant-result-title">
                              {metadata.title}
                            </h3>
                            <div className="qdrant-result-meta">
                              <div className="qdrant-type-badge">
                                {getSourceTypeInfo(metadata).label}
                              </div>
                              {(metadata.publishDate || metadata.eventDate || metadata.EventDate) && (
                                <div className="qdrant-meta-item">
                                  <FontAwesomeIcon icon={faCalendar} className="icon-margin" />
                                  {new Date(metadata.publishDate || metadata.eventDate || metadata.EventDate).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </div>
                              )}
                              {metadata.workingGroupType && (
                                <div className="qdrant-meta-item">
                                  <FontAwesomeIcon icon={faBuilding} className="icon-margin" />
                                  {metadata.workingGroupType}
                                </div>
                              )}
                              {metadata.hasTOR && (
                                <div className="qdrant-meta-item">
                                  <FontAwesomeIcon icon={faFileAlt} className="icon-margin" />
                                  Terms of Reference
                                  {metadata.torObjectiveCount > 0 && ` (${metadata.torObjectiveCount} objectives)`}
                                </div>
                              )}
                              {metadata.updateCount > 0 && (
                                <div className="qdrant-meta-item">
                                  <FontAwesomeIcon icon={faHistory} className="icon-margin" />
                                  {metadata.updateCount} updates
                                </div>
                              )}
                              {metadata.type === 'Submitted Article' && (metadata.submittedAuthor || metadata.submittedCompany) && (
                                <div className="qdrant-meta-item">
                                  <FontAwesomeIcon icon={faNewspaper} className="icon-margin" />
                                  {metadata.submittedAuthor || metadata.submittedCompany}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="qdrant-score-section">
                            <div className="qdrant-score-badge" style={{ color: getScoreIndicator(result.score).color }}>
                              <FontAwesomeIcon icon={getScoreIndicator(result.score).icon} />
                              <span>{Math.round(result.score)}%</span>
                            </div>
                            <div className="qdrant-relevance">
                              <span className="relevance-label">Relevance</span>
                              <div className="relevance-bar">
                                <div 
                                  className="relevance-fill" 
                                  style={{ 
                                    width: `${Math.min(result.score, 100)}%`,
                                    backgroundColor: getScoreIndicator(result.score).color
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Tippy 
                          content={renderTooltip(metadata)}
                          placement="bottom"
                          interactive={true}
                          maxWidth={500}
                          zIndex={99999}
                          appendTo={document.body}
                        >
                          {renderContent(metadata)}
                        </Tippy>
                        <div className="qdrant-card-footer">
                          <div className="qdrant-source-relevance">
                            <span className="relevance-label">Relevance</span>
                            <div className="relevance-bar">
                              <div 
                                className="relevance-fill" 
                                style={{ 
                                  width: `${Math.min(result.score, 100)}%`,
                                  backgroundColor: getScoreIndicator(result.score).color
                                }}
                              ></div>
                            </div>
                          </div>
                          <div className="qdrant-action-hint">
                            <FontAwesomeIcon icon={faMousePointer} />
                            <span>Click to view</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {renderPagination()}
            </>
          )}
          
          {/* Table View */}
          {activeTabIndex === 1 && (
            <>
              {renderTableView()}
              {renderPagination()}
            </>
          )}
        </div>
      )}

      {/* News Modal */}
      {activeNewsId && (
        <NewsModal
          articleId={activeNewsId}
          articleType={activeNewsType}
          show={showNewsModal}
          onClose={() => {
            setShowNewsModal(false);
            setActiveNewsId(null);
            setActiveNewsType(null);
          }}
          allowFullscreen={true}
        />
      )}

      {/* Events Modal */}
      {activeEvent && (
        <EventsModal
          data={activeEvent}
          show={showEventModal}
          onClose={() => setShowEventModal(false)}
          allowFullscreen={true}
        />
      )}

      {/* Working Group Modal */}
      {activeWorkingGroup && showWorkingGroupModal && (
        <div 
          className="wg-modal-backdrop"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowWorkingGroupModal(false);
          }}
        >
          <div 
            className="wg-modal"
            tabIndex="-1" 
            role="dialog" 
            onClick={e => e.stopPropagation()}
          >
            <div className="wg-modal-content">
              <div className="wg-modal-header">
                <h5 className="wg-modal-title">{activeWorkingGroup.title}</h5>
                <button 
                  type="button" 
                  className="wg-btn-close" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowWorkingGroupModal(false);
                  }}
                >×</button>
              </div>
              <div className="wg-modal-body">
                <div className="wg-working-group-info">
                  <div className="wg-info-section">
                    <h6>Type</h6>
                    <p>{activeWorkingGroup.workingGroupType}</p>
                  </div>
                  {activeWorkingGroup.steeringGroup && activeWorkingGroup.steeringGroup !== 'N/a' && (
                    <div className="wg-info-section">
                      <h6>Steering Group</h6>
                      <p>{activeWorkingGroup.steeringGroup}</p>
                    </div>
                  )}
                  {activeWorkingGroup.startDate && (
                    <div className="wg-info-section">
                      <h6>Timeline</h6>
                      <p>{activeWorkingGroup.startDate} - {activeWorkingGroup.endDate || 'Present'}</p>
                    </div>
                  )}
                  {activeWorkingGroup.hasTOR && (
                    <div className="wg-info-section">
                      <h6>Terms of Reference</h6>
                      <p>Available ({activeWorkingGroup.torObjectiveCount} objectives)</p>
                    </div>
                  )}
                  {activeWorkingGroup.updateCount > 0 && (
                    <div className="wg-info-section">
                      <h6>Updates</h6>
                      <p>{activeWorkingGroup.updateCount} updates available</p>
                    </div>
                  )}
                </div>
                <div className="wg-content-section mt-4">
                  <div dangerouslySetInnerHTML={{ __html: formatWorkingGroupOverview(activeWorkingGroup.text) }} />
                </div>
              </div>
              <div className="wg-modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowWorkingGroupModal(false);
                  }}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/WorkingGroups/WorkingGroup/${activeWorkingGroup.documentId}`);
                    setShowWorkingGroupModal(false);
                  }}
                >
                  View Full Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Modals */}
      {Object.values(modals).map((modal) => (
        modal.isOpen && (
          <PDFModal
            key={modal.id}
            title={modal.title}
            url={modal.pdfUrl}
            show={modal.isOpen}
            onClose={(e) => {
              e.stopPropagation();
              closePDFModal(modal.id);
            }}
            allowFullscreen={true}
            allowMove={true}
            showLinkinToolBar={true}
            style={{ top: modal.top, left: modal.left, zIndex: modal.zIndex }}
            listItemId={modal.id}
          />
        )
      ))}
    </div>
  );
}

/**
 * Export QdrantLiveSearch as default component
 * 
 * Production-ready search interface with comprehensive vector search capabilities,
 * intelligent content routing, and professional user experience features.
 */
export default QdrantLiveSearch;